import { createSelector } from 'reselect'
import { flatten, memoize } from 'lodash'

import API from '../../api/api'
import { SERVICE_CATEGORIES } from '../../helpers/constants'

export const SHOP_COLLECTION_LOAD = 'SHOP_COLLECTION_LOAD'
export const SHOP_COLLECTION_ITEM_CREATE = 'SHOP_COLLECTION_ITEM_CREATE'
export const SHOP_COLLECTION_ITEM_UPDATE = 'SHOP_COLLECTION_ITEM_UPDATE'
export const SHOP_COLLECTION_ITEM_DELETE = 'SHOP_COLLECTION_ITEM_DELETE'

export const SHOP_COLLECTION_SKU_CREATE = 'SHOP_COLLECTION_SKU_CREATE'
export const SHOP_COLLECTION_SKU_UPDATE = 'SHOP_COLLECTION_SKU_UPDATE'
export const SHOP_COLLECTION_SKU_DELETE = 'SHOP_COLLECTION_SKU_DELETE'

export const SHOP_PROMO_CODES_LOAD = 'SHOP_PROMO_CODES_LOAD'
export const SHOP_PROMO_CODES_CREATE = 'SHOP_PROMO_CODES_CREATE'
export const SHOP_PROMO_CODES_DELETE = 'SHOP_PROMO_CODES_DELETE'

const initialState = {
  collection: [],
  promoCodes: [],
}

const organisationShopReducer = (state = initialState, action) => {
  switch (action.type) {
    case SHOP_COLLECTION_LOAD: {
      return {
        ...state,
        collection: action.data,
      }
    }
    case SHOP_COLLECTION_ITEM_CREATE: {
      return {
        ...state,
        collection: [...state.collection, { ...action.data, sku: [] }],
      }
    }
    case SHOP_COLLECTION_ITEM_UPDATE: {
      return {
        ...state,
        collection: state.collection.map((el) => {
          if (el.id === action.data.id) {
            return {
              ...el,
              ...action.data,
            }
          } else {
            return el
          }
        }),
      }
    }
    case SHOP_COLLECTION_ITEM_DELETE: {
      return {
        ...state,
        collection: state.collection.filter((el) => el.id !== action.id),
      }
    }
    case SHOP_COLLECTION_SKU_CREATE: {
      const productId = action.data.productId.id
      const collection = state.collection.map((item) => {
        if (item.id === productId) {
          return {
            ...item,
            sku: [...item.sku, { ...action.data, productId: productId }],
          }
        } else {
          return item
        }
      })
      return {
        ...state,
        collection: collection,
      }
    }
    case SHOP_COLLECTION_SKU_UPDATE: {
      const productId = action.data.productId
      const collection = state.collection.map((item) => {
        if (item.id === productId) {
          return {
            ...item,
            sku: item.sku.map((sku) => {
              if (sku.id === action.data.id) {
                return action.data
              } else {
                return sku
              }
            }),
          }
        } else {
          return item
        }
      })
      return {
        ...state,
        collection: collection,
      }
    }
    case SHOP_COLLECTION_SKU_DELETE: {
      const productId = action.productId
      const collection = state.collection.map((item) => {
        if (item.id === productId) {
          return {
            ...item,
            sku: item.sku.filter((sku) => sku.id !== action.skuId),
          }
        } else {
          return item
        }
      })
      return {
        ...state,
        collection: collection,
      }
    }
    case SHOP_PROMO_CODES_LOAD: {
      return {
        ...state,
        promoCodes: action.data,
      }
    }
    case SHOP_PROMO_CODES_CREATE: {
      return {
        ...state,
        promoCodes: [action.data, ...state.promoCodes],
      }
    }
    case SHOP_PROMO_CODES_DELETE: {
      return {
        ...state,
        promoCodes: state.promoCodes.filter((el) => el.hash !== action.hash),
      }
    }
    default:
      return state
  }
}

export const fetchShopCollection = () => {
  return function (dispatch) {
    return API.getAllProducts()
      .then((res) => {
        dispatch({
          type: SHOP_COLLECTION_LOAD,
          data: res,
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const createShopCollectionItem = (data) => {
  return (dispatch) => {
    return API.createNewProduct(data)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_ITEM_CREATE, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const updateShopCollectionItem = (data, id) => {
  return (dispatch) => {
    return API.updateProductData(data, id)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_ITEM_UPDATE, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const deleteShopCollectionItem = (id) => {
  return (dispatch) => {
    return API.deleteProduct(id)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_ITEM_DELETE, id })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const createShopSKU = (data) => {
  return (dispatch) => {
    return API.createNewSKU(data)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_SKU_CREATE, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const updateShopSKU = (data, id) => {
  return (dispatch) => {
    return API.updateSKUData(data, id)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_SKU_UPDATE, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const deleteShopSKU = (skuId, productId) => {
  return (dispatch) => {
    return API.deleteSKU(skuId)
      .then((res) => {
        dispatch({ type: SHOP_COLLECTION_SKU_DELETE, skuId, productId })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const fetchPromoCodes = () => {
  return (dispatch) => {
    return API.getAllPromoCodes()
      .then((res) => {
        dispatch({ type: SHOP_PROMO_CODES_LOAD, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const createPromoCode = (data) => {
  return (dispatch) => {
    return API.createNewPromoCode(data)
      .then((res) => {
        dispatch({ type: SHOP_PROMO_CODES_CREATE, data: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const deletePromoCode = (hash) => {
  return (dispatch) => {
    return API.deletePromoCode(hash)
      .then((res) => {
        dispatch({ type: SHOP_PROMO_CODES_DELETE, hash })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

// COLLECTION
export const formatShopCollectionForSelect = (items) =>
  items.map((item) => ({ value: item.id, label: item.name }))

export const selectShopCollection = (state) => state.organisationShop.collection

export const selectShopCollectionWithoutGifts = createSelector(selectShopCollection, (collection) =>
  collection.filter((product) => product.category !== 'Gift'),
)

// SERVICES
export const selectShopServices = createSelector(selectShopCollection, (collection) =>
  collection.filter((item) => SERVICE_CATEGORIES.includes(item.category)),
)

export const selectShopServiceById = createSelector(selectShopServices, (services) =>
  memoize((id) => {
    if (id === 'new') {
      return {}
    }
    return services.find((service) => service.id === parseInt(id)) || {}
  }),
)

// PRODUCTS
export const selectShopProducts = createSelector(selectShopCollection, (collection) =>
  collection.filter((item) => item.category === 'Product'),
)

export const selectShopProductById = createSelector(selectShopProducts, (products) =>
  memoize((id) => {
    if (id === 'new') {
      return {}
    }
    return products.find((product) => product.id === parseInt(id)) || {}
  }),
)

// SKUs
export const selectShopCollectionSKUs = createSelector(selectShopCollection, (collection) =>
  flatten(collection.map((item) => item.sku)),
)

export const selectShopSKUById = createSelector(selectShopCollectionSKUs, (SKUs) =>
  memoize((skuId) => {
    if (skuId === 'new') {
      return {}
    }
    return SKUs.find((sku) => sku.id === parseInt(skuId)) || {}
  }),
)

// PROMO CODES
export const selectPromoCodes = (state) => state.organisationShop.promoCodes

export const selectPromoCodeById = createSelector(selectPromoCodes, (promoCodes) =>
  memoize((id) => {
    if (id === 'new') {
      return {}
    }
    return promoCodes.find((code) => code.id === parseInt(id)) || {}
  }),
)

export default organisationShopReducer
